// Regions data for eSIM packages
// Maps regions to country codes for API calls

export interface Region {
  id: string;
  name: string;
  emoji: string;
  countryCodes: string[]; // ISO alpha-2 country codes
  type: "region";
}

/**
 * Country code aliases mapping
 * Maps common country code variations to ISO alpha-2 standard codes
 * Handles cases where non-standard codes are used instead of ISO alpha-2
 */
const COUNTRY_CODE_ALIASES: Record<string, string> = {
  UK: "GB", // United Kingdom: UK is commonly used but GB is ISO alpha-2 standard
  // Note: USA -> US is not needed as both are valid, but if you receive "USA" as 3-letter code:
  // USA: "US", // Uncomment if you need to handle "USA" as input
  // Add more aliases as needed when non-standard codes are encountered
};

/**
 * Normalize country code to ISO alpha-2 standard
 * Handles aliases like UK -> GB
 * @param countryCode - Country code (can be alias or standard ISO code)
 * @returns Normalized ISO alpha-2 country code
 */
export function normalizeCountryCode(countryCode: string): string {
  const upperCode = countryCode.toUpperCase();
  return COUNTRY_CODE_ALIASES[upperCode] || upperCode;
}

export const REGIONS: Region[] = [
  {
    id: "asia",
    name: "Asia",
    emoji: "🌏",
    countryCodes: [
      "AF", "AM", "AZ", "BH", "BD", "BT", "BN", "KH", "CN", "GE",
      "HK", "IN", "ID", "IR", "IQ", "IL", "JP", "JO", "KZ", "KW",
      "KG", "LA", "LB", "MY", "MV", "MN", "MM", "NP", "KP", "OM",
      "PK", "PS", "PH", "QA", "SA", "SG", "KR", "LK", "SY", "TW",
      "TJ", "TH", "TL", "TR", "AE", "UZ", "VN", "YE"
    ],
    type: "region",
  },
  {
    id: "europe",
    name: "Europe",
    emoji: "🇪🇺",
    countryCodes: [
      "AL", "AD", "AT", "BY", "BE", "BA", "BG", "HR", "CY", "CZ",
      "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IS", "IE", "IT",
      "LV", "LI", "LT", "LU", "MT", "MD", "MC", "ME", "NL", "MK",
      "NO", "PL", "PT", "RO", "RU", "SM", "RS", "SK", "SI", "ES",
      "SE", "CH", "UA", "GB", "VA"
    ],
    type: "region",
  },
  {
    id: "americas",
    name: "Americas",
    emoji: "🌎",
    countryCodes: [
      "AR", "BS", "BB", "BZ", "BO", "BR", "CA", "CL", "CO", "CR",
      "CU", "DM", "DO", "EC", "SV", "GD", "GT", "GY", "HT", "HN",
      "JM", "MX", "NI", "PA", "PY", "PE", "KN", "LC", "VC", "SR",
      "TT", "US", "UY", "VE"
    ],
    type: "region",
  },
  {
    id: "africa",
    name: "Africa",
    emoji: "🌍",
    countryCodes: [
      "DZ", "AO", "BJ", "BW", "BF", "BI", "CV", "CM", "CF", "TD",
      "KM", "CG", "CD", "CI", "DJ", "EG", "GQ", "ER", "SZ", "ET",
      "GA", "GM", "GH", "GN", "GW", "KE", "LS", "LR", "LY", "MG",
      "MW", "ML", "MR", "MU", "MA", "MZ", "NA", "NE", "NG", "RW",
      "ST", "SN", "SC", "SL", "SO", "ZA", "SS", "SD", "TZ", "TG",
      "TN", "UG", "ZM", "ZW"
    ],
    type: "region",
  },
  {
    id: "oceania",
    name: "Oceania",
    emoji: "🌊",
    countryCodes: [
      "AU", "FJ", "KI", "MH", "FM", "NR", "NZ", "PW", "PG", "WS",
      "SB", "TO", "TV", "VU"
    ],
    type: "region",
  },
  {
    id: "middle-east",
    name: "Middle East",
    emoji: "🕌",
    countryCodes: [
      "BH", "IR", "IQ", "IL", "JO", "KW", "LB", "OM", "PS", "QA",
      "SA", "SY", "AE", "YE"
    ],
    type: "region",
  },
];

/**
 * Get region by ID
 */
export function getRegionById(id: string): Region | undefined {
  return REGIONS.find((region) => region.id === id);
}

/**
 * Get all regions
 */
export function getAllRegions(): Region[] {
  return REGIONS;
}

/**
 * Check if a country code belongs to a region
 * Handles country code aliases (e.g., UK -> GB)
 */
export function isCountryInRegion(countryCode: string, regionId: string): boolean {
  const region = getRegionById(regionId);
  if (!region) return false;
  const normalizedCode = normalizeCountryCode(countryCode);
  return region.countryCodes.includes(normalizedCode);
}

/**
 * Get all regions that contain a specific country code
 * A country can belong to multiple regions (e.g., Middle East countries also in Asia)
 * Handles country code aliases (e.g., UK -> GB)
 * @param countryCode - ISO alpha-2 country code or alias (e.g., "US", "VN", "UK")
 * @returns Array of regions that contain this country
 */
export function getRegionsByCountryCode(countryCode: string): Region[] {
  const normalizedCode = normalizeCountryCode(countryCode);
  return REGIONS.filter((region) => 
    region.countryCodes.includes(normalizedCode)
  );
}

/**
 * Get the first region that contains a specific country code
 * Useful when you only need one region for a country
 * Handles country code aliases (e.g., UK -> GB)
 * @param countryCode - ISO alpha-2 country code or alias (e.g., "US", "VN", "UK")
 * @returns First region found, or undefined if country not found in any region
 */
export function getRegionByCountryCode(countryCode: string): Region | undefined {
  const normalizedCode = normalizeCountryCode(countryCode);
  return REGIONS.find((region) => 
    region.countryCodes.includes(normalizedCode)
  );
}

/**
 * Create a Map for fast lookup of country code to regions
 * Useful for performance optimization when doing many lookups
 * Includes both standard codes and aliases in the map
 * @returns Map where key is country code (uppercase) and value is array of region IDs
 */
export function createCountryToRegionsMap(): Map<string, string[]> {
  const map = new Map<string, string[]>();
  
  REGIONS.forEach((region) => {
    region.countryCodes.forEach((countryCode) => {
      const normalizedCode = countryCode.toUpperCase();
      const existingRegions = map.get(normalizedCode) || [];
      if (!existingRegions.includes(region.id)) {
        existingRegions.push(region.id);
        map.set(normalizedCode, existingRegions);
      }
      
      // Also add aliases that map to this code
      Object.entries(COUNTRY_CODE_ALIASES).forEach(([alias, standardCode]) => {
        if (standardCode === normalizedCode) {
          const aliasRegions = map.get(alias) || [];
          if (!aliasRegions.includes(region.id)) {
            aliasRegions.push(region.id);
            map.set(alias, aliasRegions);
          }
        }
      });
    });
  });
  
  return map;
}

